import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { ACCESSES_URL } from '../api-urls'
import { CacheableDataProviderExtensionResult } from '../common/data-provider'
import { DateTime } from '../common/date-time'
import { get, getWithPagination, patch, post, put } from '../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../common/ticket-api-get-list.params'
import { AccessVariantsDto } from '../dto/access-variants.dto'
import { AccessDto } from '../dto/access.dto'
import { AccessActivePeriodStartingOptions } from '../enum/AccessActivePeriodStartingOptions'
import { DayOfWeek } from '../enum/DayOfWeek'
import filterMapper from './access-filter.mapper'
import { mapSortAccessParam } from './access-sort.mapper'
import { AccessTicketPeriodOptions } from '../enum/AccessTicketPeriodOptions'
import { DeferLogicOptions } from '../enum/DeferLogicOptions'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<AccessDto>> => {
    const filterParams = `a=>${filterParamsComposer('a', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortAccessParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<AccessDto[]>(
      `${ACCESSES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<AccessDto>> => {
    const data = await get<AccessDto>(ACCESSES_URL, `/${id}`)
    return {
      data,
    }
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<AccessDto>> => {
    const data = await get<AccessDto[]>(
      `${ACCESSES_URL}/GetByConditions`,
      `/u=>new int[] {${ids.toString()}}.Contains(u.Id)`,
    )
    return Promise.resolve({
      data,
    })
  },
  update: async (
    resource: string,
    { data, previousData }: UpdateParams<AccessDto>,
  ): Promise<UpdateResult> => {
    const updatePayload: Partial<UpdateAccessRequest> = {}

    Object.keys(data).forEach((key) => {
      if (['id', 'accessDatesIds', 'variantsIds'].includes(key)) return

      if (data?.[key] !== undefined && data[key] !== previousData[key]) {
        updatePayload[key] = data[key]
      }
    })

    const fullUpdate: UpdateAccessRequest = {
      id: data.id,
      ...updatePayload,
    }

    const update = await patch<UpdateAccessRequest, AccessDto>(
      ACCESSES_URL,
      fullUpdate,
    )
    return Promise.resolve({ data: update })
  },
  create: async (
    resource: string,
    { data }: CreateParams<AccessDto>,
  ): Promise<CreateResult> => {
    const created = await post<CreateAccessRequest[], AccessDto[]>(
      ACCESSES_URL,
      [
        {
          name: data.name,
          bonusId: data.bonusId ? data.bonusId : undefined,
          entranceId: data.entranceId || undefined,
          allowReenter: data.allowReenter || undefined,
          multipleExit: data.multipleExit || undefined,
          multiplicity: data.multiplicity || undefined,
          usePriority: data.usePriority || undefined,
          ticketUnlockOption: data.ticketUnlockOption || undefined,
          unlockTime: data.unlockTime || undefined,
          unlockDayOfWeek: data.unlockDayOfWeek || undefined,
          unlockDayOfMonth: data.unlockDayOfMonth || undefined,
          unlockDelay: data.unlockDelay || undefined,
          ticketDeferOption: data.ticketDeferOption || undefined,
          deferTime: data.deferTime || undefined,
          deferDayOfWeek: data.deferDayOfWeek || undefined,
          deferDayOfMonth: data.deferDayOfMonth || undefined,
          deferDelay: data.deferDelay || undefined,
          deferLogic: data.deferLogic || undefined,
          activePeriod: data.activePeriod || undefined,
          activePeriodStartingFrom: data.activePeriodStartingFrom || undefined,
          restrictLicencePlate: data.restrictLicencePlate || undefined,
          allowAddLicencePlate: data.allowAddLicencePlate || undefined,
        },
      ],
    )
    return Promise.resolve({ data: created[0] })
  },
  attachVariants: async (
    resource: string,
    params: AccessVariantsDto,
  ): Promise<CacheableDataProviderExtensionResult<AccessVariantsDto>> => {
    const accessVariants = await put<AccessVariantsDto, AccessVariantsDto>(
      `${ACCESSES_URL}/AttachVariants`,
      params,
    )
    return {
      data: accessVariants,
    }
  },
  detachVariants: async (
    resource: string,
    params: AccessVariantsDto,
  ): Promise<CacheableDataProviderExtensionResult<AccessVariantsDto>> => {
    const accessVariants = await put<AccessVariantsDto, AccessVariantsDto>(
      `${ACCESSES_URL}/DetachVariants`,
      params,
    )
    return {
      data: accessVariants,
    }
  },
} as AccessDataProvider

interface AccessDataProvider extends DataProvider {
  attachVariants: (
    resource: string,
    params: AccessVariantsDto,
  ) => Promise<CacheableDataProviderExtensionResult<AccessVariantsDto>>

  detachVariants: (
    resource: string,
    params: AccessVariantsDto,
  ) => Promise<CacheableDataProviderExtensionResult<AccessVariantsDto>>
}

interface CreateAccessRequest {
  readonly name: string
  readonly bonusId?: number
  readonly entranceId?: number
  readonly allowReenter?: boolean
  // readonly multipleEntry?: boolean
  readonly multipleExit?: boolean
  // readonly multiple?: number
  readonly multiplicity?: number
  readonly usePriority?: number
  // readonly isTicketUnlockedWithNextDay?: boolean
  // readonly releaseTime?: DateTime
  // readonly releaseDayOfWeek?: DayOfWeek
  // readonly releaseDayOfMonth?: number
  // readonly delay?: string
  readonly ticketUnlockOption?: AccessTicketPeriodOptions
  readonly unlockTime?: DateTime
  readonly unlockDayOfWeek?: DayOfWeek
  readonly unlockDayOfMonth?: number
  readonly unlockDelay?: string
  readonly ticketDeferOption?: AccessTicketPeriodOptions
  readonly deferTime?: DateTime
  readonly deferDayOfWeek?: DayOfWeek
  readonly deferDayOfMonth?: number
  readonly deferDelay?: string
  readonly deferLogic?: DeferLogicOptions
  readonly activePeriod?: string
  readonly activePeriodStartingFrom?: AccessActivePeriodStartingOptions
  readonly restrictLicencePlate?: boolean
  readonly allowAddLicencePlate?: boolean
}

interface UpdateAccessRequest {
  readonly id: Identifier
  readonly name?: string
  readonly bonusId?: number
  readonly entranceId?: number
  readonly allowReenter?: boolean
  readonly multipleExit?: boolean
  readonly multiplicity?: number
  readonly usePriority?: number
  readonly ticketUnlockOption?: AccessTicketPeriodOptions
  readonly unlockTime?: DateTime
  readonly unlockDayOfWeek?: DayOfWeek
  readonly unlockDayOfMonth?: number
  readonly unlockDelay?: string
  readonly ticketDeferOption?: AccessTicketPeriodOptions
  readonly deferTime?: DateTime
  readonly deferDayOfWeek?: DayOfWeek
  readonly deferDayOfMonth?: number
  readonly deferDelay?: string
  readonly deferLogic?: DeferLogicOptions
  readonly activePeriod?: string
  readonly activePeriodStartingFrom?: AccessActivePeriodStartingOptions
  readonly restrictLicencePlate?: boolean
  readonly allowAddLicencePlate?: boolean
}

export default provider
